import type {SerializedError} from '@reduxjs/toolkit'
import type {FetchBaseQueryError} from '@reduxjs/toolkit/dist/query'
import type {Dispatch, FC, ReactElement, ReactNode, SetStateAction} from 'react'
import {createContext, useContext, useEffect, useMemo, useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {useDispatch} from 'react-redux'
import {useLocation, useNavigate} from 'react-router-dom'
import type {AuthContextProps} from './AuthProvider'
import {useGetExternalBookmarksQuery} from '@apis/base'
import {useLazyGetTenantsCurrentUserQuery} from '@apis/infrastructureTenants'
import {useAuthContext} from '@containers/App/AuthProvider'
import {handleUnauthorized} from '@apis/helpers'
import {getToken} from '@app/util-functions'
import type {FeatureFlag} from '@shared/constants/featureFlags'
import type {UserInterface} from '@interfaces/user'
import {setPreferredLanguage, setPreferredLocale, setPreferredTheme} from '@redux/slices/user.ts'
import {mergeTenantUserIntoCurrentUser} from '@shared/utils/tenantUser'
import {isDashboardPrintPath} from '@shared/utils/printRoute'

interface UserProviderProps {
  children?: ReactNode
}

export interface UserContextProps {
  currentUser: UserInterface | null
  setCurrentUser: Dispatch<SetStateAction<UserInterface | null>>
  homeViewTransitionTarget: 'legacy' | 'new' | null
  setHomeViewTransitionTarget: Dispatch<SetStateAction<'legacy' | 'new' | null>>
  setLoadingUser: Dispatch<SetStateAction<boolean>>
  loadingUser?: boolean
  userError?: FetchBaseQueryError | SerializedError | undefined
  welcomeSkipped?: boolean
  setWelcomeSkipped: Dispatch<SetStateAction<boolean>>
  hasExternalBookmarks: boolean
  featureFlags: Set<FeatureFlag>
}

const UserContext = createContext<UserContextProps | null>(null)

const useUserContext = (): UserContextProps | null => useContext(UserContext)

const UserProvider: FC<UserProviderProps> = ({children}): ReactElement => {
  const location = useLocation()
  const {pathname} = window.location
  const allowedPaths = [
    'compare-pdfs',
    'orders',
    'dashboard',
    'create-account',
    'authentication',
    'authorize-callback',
    'code',
    // The shop-floor display (AB#6756): the screen has no person signed in, and
    // its pair page sends a signed-out approver to sign in itself, code kept.
    'display',
  ]
  const authContext = useAuthContext() as AuthContextProps
  const {userIsLoggedIn} = authContext
  const [loadingUser, setLoadingUser] = useState<boolean>(true)
  // Bumped to load the user once more after an "Invalid token." was refreshed.
  const [userLoadAttempt, setUserLoadAttempt] = useState(0)
  const invalidTokenRetriedRef = useRef(false)
  const [welcomeSkipped, setWelcomeSkipped] = useState<boolean>(false)
  const [currentUser, setCurrentUser] = useState<UserInterface | null>(null)
  const [homeViewTransitionTarget, setHomeViewTransitionTarget] = useState<'legacy' | 'new' | null>(
    null,
  )
  const navigate = useNavigate()
  const [getTenantsCurrentUser] = useLazyGetTenantsCurrentUserQuery()
  const dispatch = useDispatch()
  const {i18n} = useTranslation()
  const {data: externalBookmarks} = useGetExternalBookmarksQuery(
    {limit: 1, offset: 0},
    {skip: !userIsLoggedIn},
  )
  const hasExternalBookmarks = (externalBookmarks?.results?.length ?? 0) > 0

  useEffect(() => {
    if (userIsLoggedIn) {
      let cancelled = false
      let handlingInvalidToken = false
      // For the "already rotated" check if the token turns out to be invalid.
      const requestToken = getToken()
      setLoadingUser(true)

      getTenantsCurrentUser()
        .unwrap()
        .then((tenantsUser) => {
          if (cancelled) {
            return
          }
          invalidTokenRetriedRef.current = false
          const user = mergeTenantUserIntoCurrentUser(null, tenantsUser)
          setCurrentUser(user)
          // If the user is inactive, redirect to the inactive user page
          if (user.is_active === false) {
            navigate('/inactive_user/')
            return
          }

          if (user.userprofile) {
            dispatch(setPreferredTheme(user.userprofile.preferred_theme))
            dispatch(setPreferredLanguage(user.userprofile.preferred_language))
            dispatch(setPreferredLocale(user.userprofile.preferred_locale))

            if (user.userprofile.preferred_language) {
              i18n.changeLanguage(user.userprofile.preferred_language)
            }
          }
        })
        .catch((error) => {
          if (cancelled) {
            return
          }
          // If the token is invalid. Not on the print route (AB#6645): the
          // renderer has no login to go to, the page reports the error itself.
          if (isDashboardPrintPath()) {
            return
          }
          // If the token is invalid: the shared 401 handling (refresh, else a
          // silent re-authorize against the auth site's session) instead of a
          // logout (AB#6617), and one more load if it refreshed.
          if (error.data?.detail === 'Invalid token.') {
            handlingInvalidToken = true
            void handleUnauthorized({authContext, dispatch, requestToken}).then((shouldRetry) => {
              if (cancelled) {
                return
              }
              if (shouldRetry && !invalidTokenRetriedRef.current) {
                invalidTokenRetriedRef.current = true
                setUserLoadAttempt((attempt) => attempt + 1)
                return
              }
              setLoadingUser(false)
            })
          } else if (error.data?.detail === 'User inactive or deleted.') {
            navigate('/inactive_user/?status=user-not-found')
          }
        })
        .finally(() => {
          if (!cancelled && !handlingInvalidToken) {
            setLoadingUser(false)
          }
        })

      return () => {
        cancelled = true
      }
    } else {
      setCurrentUser(null)
      setLoadingUser(false)
      if (!allowedPaths.includes(pathname.split('/')[1])) {
        navigate('/login')
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userIsLoggedIn, userLoadAttempt])

  useEffect(() => {
    if (homeViewTransitionTarget === 'new' && location.pathname.startsWith('/home')) {
      setHomeViewTransitionTarget(null)
    }

    if (homeViewTransitionTarget === 'legacy' && location.pathname.startsWith('/lines_overview')) {
      setHomeViewTransitionTarget(null)
    }
  }, [homeViewTransitionTarget, location.pathname])

  const featureFlags = useMemo(() => {
    const flags = new Set<FeatureFlag>(
      (currentUser?.userprofile?.feature_flags ??
        currentUser?.userprofile?.effective_feature_flags ??
        []) as FeatureFlag[],
    )
    // Dev-only override: flags normally arrive via the auth provider's
    // tenant-user payload, which local setups (e.g. the E2E stack's seeded
    // token) skip. Two sources, merged: `VITE_DEV_FEATURE_FLAGS` from the
    // env file (e.g. .env.e2e.local — zero-setup for the E2E stack) and
    // `localStorage.mbai_dev_feature_flags = 'a,b'` for ad-hoc toggling.
    // Never active in production builds.
    if (import.meta.env.DEV) {
      const overrides = [
        (import.meta.env.VITE_DEV_FEATURE_FLAGS as string | undefined) ?? '',
        localStorage.getItem('mbai_dev_feature_flags') ?? '',
      ]
        .join(',')
        .split(',')
        .map((value) => value.trim())
        .filter(Boolean)
      for (const flag of overrides) {
        flags.add(flag as FeatureFlag)
      }
    }
    return flags
  }, [currentUser])

  return (
    <UserContext.Provider
      value={useMemo(
        () => ({
          currentUser,
          setCurrentUser,
          homeViewTransitionTarget,
          setHomeViewTransitionTarget,
          loadingUser,
          setLoadingUser,
          welcomeSkipped,
          setWelcomeSkipped,
          hasExternalBookmarks,
          featureFlags,
        }),
        [
          currentUser,
          setCurrentUser,
          homeViewTransitionTarget,
          setHomeViewTransitionTarget,
          loadingUser,
          setLoadingUser,
          welcomeSkipped,
          setWelcomeSkipped,
          hasExternalBookmarks,
          featureFlags,
        ],
      )}
    >
      {children}
    </UserContext.Provider>
  )
}

export {useUserContext, UserProvider, UserContext}
